import { useState, useEffect } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useUpdatePublicationNotes } from '../../../api/media-sim/publications';
import type { Publication } from '../../../types/media-sim';

interface PublicationNotesEditorProps {
  publication: Publication;
  isAdmin: boolean;
  onSaved?: () => void;
}

export function PublicationNotesEditor({ publication, isAdmin, onSaved }: PublicationNotesEditorProps) {
  const { theme } = useTheme();
  const { updateNotes, loading } = useUpdatePublicationNotes();
  const [notes, setNotes] = useState(publication.publication_notes || '');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    setNotes(publication.publication_notes || '');
    setSaveStatus('idle');
  }, [publication.id, publication.publication_notes]);

  const handleSave = async () => {
    setSaveStatus('idle');
    const result = await updateNotes(publication.id, notes || null);
    if (result) {
      setSaveStatus('saved');
      onSaved?.();
      setTimeout(() => setSaveStatus('idle'), 2000);
    } else {
      setSaveStatus('error');
    }
  };

  const hasChanges = notes !== (publication.publication_notes || '');

  return (
    <Card variant="outlined">
      <Card.Header
        title="Publication Notes"
        subtitle="You may include additional expert notes on how this publication covers and writes stories to be included in their publication style profile."
      />
      <Card.Body>
        <Stack gap="sm">
          <textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setSaveStatus('idle');
            }}
            placeholder="Add notes about this publication..."
            rows={4}
            style={{
              width: '100%',
              padding: spacing.sm,
              fontSize: '0.9rem',
              lineHeight: 1.6,
              fontFamily: 'inherit',
              border: `1px solid ${theme.colors.border.default}`,
              borderRadius: '6px',
              backgroundColor: theme.colors.background.default,
              color: theme.colors.text.primary,
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = theme.colors.primary[500];
            }}
            onBlur={(e) => {
              e.target.style.borderColor = theme.colors.border.default;
            }}
          />
          <Flex justify="between" align="center">
            <div>
              {saveStatus === 'saved' && (
                <span style={{ color: theme.colors.semantic.success.default, fontSize: '0.85rem', fontWeight: 500 }}>
                  Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>
                  Failed to save. Please try again.
                </span>
              )}
            </div>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={handleSave}
              disabled={loading || !hasChanges}
            >
              {loading ? 'Saving...' : 'Save Notes'}
            </Button>
          </Flex>
        </Stack>
      </Card.Body>
    </Card>
  );
}
